import React, { useEffect, useState } from 'react';
import { SOURCES_DATA } from '../../static/data';
import { disconnect } from '../../api/disconnect';
import { useTelegram } from '../../contexts/TelegramContext';
import { Source, UserData } from '../../types';
import SourceCard from '../cards/SourceCard';
import WhatsAppQRModal from '../WhatsAppQRModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { connectSource } from '../../api/auth';

type Props = {
  user: UserData;
};

const SourcesTab: React.FC<Props> = ({ user }) => {
  const { tgApp } = useTelegram();
  const [showWhatsAppQR, setShowWhatsAppQR] = useState(false);

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (sourcePath: string) => disconnect(sourcePath),
    onError: (error) => alert(`Error: ${error.message}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing data...');
        queryClient.invalidateQueries({ queryKey: ['user'] });
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'AUTH_SUCCESS') {
        console.log('Auth success message received, refreshing data...');
        queryClient.invalidateQueries({ queryKey: ['user'] });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('message', handleMessage);

    return () => {
      console.log('SourcesTab unmounting, cleaning up...');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleSourceToggle = async (source: Source) => {
    const connected = user?.[source.key as keyof UserData];

    if (connected) {
      if (!window.confirm(`Disconnect ${source.name}?`)) return;

      mutate(source.path);
    } else if (source.requiresQR) {
      setShowWhatsAppQR(true);
    } else {
      const url = await connectSource(source.path);
      tgApp?.openLink(url);
    }
  };

  return (
    <>
      {showWhatsAppQR && (
        <WhatsAppQRModal onClose={() => setShowWhatsAppQR(false)} />
      )}
    <div className='mt-[20px] space-y-6'>
      <div className='flex flex-col font-medium'>
        <h2 className='text-lg text-white'>Connect your data sources:</h2>

        <span className='text-xs text-coral-2'>
          Earn income quickly and securely by connecting your profiles from
          trusted platforms:
        </span>
      </div>

      <div className='flex flex-col gap-3'>
        {SOURCES_DATA.map((source) => (
          <SourceCard
            key={source.key}
            source={source}
            connected={!!user?.[source.key as keyof UserData]}
            onToggle={() => handleSourceToggle(source)}
          />
        ))}
      </div>
    </div>
    </>
  );
};

export default SourcesTab;
